import * as React from "react";
import moment from "moment";
import { Video } from "lucide-react";
import { Badge, Card } from "@/components/ui";
import { type Category, type SuperPageAssignment } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { SuperPageAssignmentStatus, timestampLabel } from "./SuperPageAssignmentStatus";

type Props = {
  assignment?: SuperPageAssignment;
  disabled?: boolean;
  onClick?: () => void;
  dataSkeleton?: boolean;
};

const formatAssignmentDate = (assignment: SuperPageAssignment): string => {
  if (assignment.dueDate) {
    return `Due ${moment(assignment.dueDate).format("MMM D")}`;
  }
  return timestampLabel(assignment.createdDate, "Added");
};

const CategoryScrollRow: React.FC<{ categories: Category[] }> = ({ categories }) => {
  return (
    <div className="relative -mx-2.5 mb-2">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-2.5 bg-gradient-to-r from-card to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-2.5 bg-gradient-to-l from-card to-transparent"
      />
      <div className="overflow-x-auto no-scrollbar py-0.5">
        <div className="flex w-max flex-nowrap gap-1 px-2.5">
          {categories.map(category => (
            <Badge
              key={`category-${category.id}`}
              className="pointer-events-none w-fit max-w-32 shrink-0 font-normal select-none"
              size="small"
              variant="label"
              title={category.name}
            >
              <span className="block max-w-full truncate">{category.name}</span>
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

const SuperPageAssignmentCard: React.FC<Props> = ({ assignment, disabled = false, onClick, dataSkeleton = false }) => {
  const categories = assignment?.superPage?.categories;
  const thumbnailUrl = assignment?.thumbnailUrl;

  return (
    <Card
      disabled={disabled}
      data-skeleton={dataSkeleton}
      className={cn(
        "group relative w-43 min-w-43 cursor-pointer gap-0 overflow-hidden border-0 py-0 shadow-sm ring-1 ring-border/50 transition-all duration-200 hover:shadow-md hover:ring-border ignore-base-styling",
        disabled && "pointer-events-none opacity-50"
      )}
      onClick={() => onClick?.()}
    >
      <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden border-b border-border/40 bg-muted">
        {assignment && (
          <div className="absolute top-2 left-2 z-10">
            <SuperPageAssignmentStatus status={assignment.status} viewedAt={assignment.viewedAt} />
          </div>
        )}
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Video className="size-8 text-muted-foreground" strokeWidth={1.5} />
          </div>
        )}
      </div>

      <Card.Content className="flex min-h-24 flex-col overflow-visible px-2.5 py-2">
        {categories && categories.length > 0 && <CategoryScrollRow categories={categories} />}
        <div className="flex min-h-12 flex-1 flex-col gap-1">
          <Card.Title className="ignore-base-styling m-0 line-clamp-2 min-h-8 text-xs font-medium leading-snug text-foreground">
            {assignment?.superPage?.title || <span className="text-muted-foreground italic">No title</span>}
          </Card.Title>
          {assignment && (
            <Card.Description className="ignore-base-styling mt-auto shrink-0 text-[11px] leading-tight text-muted-foreground">
              {formatAssignmentDate(assignment)}
            </Card.Description>
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

export { SuperPageAssignmentCard };
